import { useLocation, useNavigationType, Location } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Action } from "@remix-run/router";
import { focusMainContent } from "../../../../../common/utils/focus.ts";

// focus main when the route loads or re-loads so screen reader
// and keyboard users are where they need to be, main is aria-labelled
// with page specific label to make this obvious to the screen reader user

export function useRouteMainFocus() {
  const navType = useNavigationType();
  const { pathname, key, state, search } = useLocation() as Location<{
    isRedirect: boolean;
  }>;
  const initialPath = useRef(
    pathname.endsWith("/") ? pathname.slice(0, -1) : pathname
  );
  const initialNavType = useRef<Action | null>(navType);
  const initialSearch = useRef(search);

  useEffect(() => {
    if (navType !== Action.Pop && initialNavType.current === Action.Pop) {
      initialNavType.current = null;
    }
  }, [navType]);

  useEffect(() => {
    // only want to focus on internal nav, a full page load
    // should leave focus at top of doc as normal,
    // key is a dependency to refocus from unchanged route
    // initialPath used to prevent refocus from child route
    // initialNavType used to handle redirects when mounted
    // an isRedirect flag is used to detect redirection before mount
    // reader -> library return deep link can temp refocus main so avoid that
    const navTypeToUse = initialNavType.current ?? navType;
    if (
      pathname === initialPath.current &&
      (navTypeToUse === Action.Push || navTypeToUse === Action.Replace) &&
      state?.isRedirect !== true &&
      !initialSearch.current.includes("library_return_detail")
    ) {
      focusMainContent();
    }
  }, [navType, initialNavType, key, pathname, state?.isRedirect, search]);
}
